import { useEffect, useRef } from "react";

/**
 * 로그인 화면 뒤의 별자리 배경 — 점들이 천천히 떠다니고 가까운 점끼리 실선으로 이어진다.
 * 사이에 옅은 육각형·삼각형 윤곽이 회전한다 (레퍼런스 triznrnd.kro.kr 의 느낌).
 *
 * 의존성 없음. `prefers-reduced-motion` 이면 정지 화면 한 장만 그린다.
 * 색은 팔레트 토큰과 같다 — CSS 변수를 읽어 쓰므로 팔레트를 바꾸면 같이 바뀐다.
 */
export function NetworkCanvas({ dim = false }: { dim?: boolean }) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const css = getComputedStyle(document.documentElement);
    const c1 = css.getPropertyValue("--p3").trim() || "#7AA192";
    const c2 = css.getPropertyValue("--p4").trim() || "#356855";
    const c3 = css.getPropertyValue("--p2").trim() || "#A6CBBC";

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    type Dot = { x: number; y: number; vx: number; vy: number; r: number };
    let dots: Dot[] = [];
    const shapes = [
      { x: 0.12, y: 0.68, n: 6, r: 120, a: 0, s: 0.0006 },
      { x: 0.8, y: 0.74, n: 3, r: 110, a: 0.6, s: -0.0008 },
      { x: 0.86, y: 0.16, n: 6, r: 70, a: 1.1, s: 0.0009 },
    ];

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas!.width = Math.floor(width * dpr);
      canvas!.height = Math.floor(height * dpr);
      canvas!.style.width = `${width}px`;
      canvas!.style.height = `${height}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(28, Math.min(70, Math.floor((width * height) / 22000)));
      dots = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.2 + Math.random() * 1.8,
      }));
    }

    function hex(color: string, alpha: number) {
      const m = /^#?([0-9a-f]{6})$/i.exec(color);
      if (!m) return color;
      const n = parseInt(m[1]!, 16);
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
    }

    function draw(t: number) {
      ctx!.clearRect(0, 0, width, height);

      // 옅은 다각형 윤곽
      for (const s of shapes) {
        const cx = s.x * width;
        const cy = s.y * height;
        const a = s.a + t * s.s;
        ctx!.beginPath();
        for (let i = 0; i <= s.n; i++) {
          const ang = a + (i / s.n) * Math.PI * 2;
          const px = cx + Math.cos(ang) * s.r;
          const py = cy + Math.sin(ang) * s.r;
          if (i === 0) ctx!.moveTo(px, py);
          else ctx!.lineTo(px, py);
        }
        ctx!.strokeStyle = hex(c3, 0.55);
        ctx!.lineWidth = 1;
        ctx!.stroke();
      }

      // 점끼리 잇기
      const link = 150;
      for (let i = 0; i < dots.length; i++) {
        const a = dots[i]!;
        for (let j = i + 1; j < dots.length; j++) {
          const b = dots[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < link) {
            ctx!.strokeStyle = hex(c1, (1 - d / link) * 0.35);
            ctx!.lineWidth = 1;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }
      }

      // 점
      for (const d of dots) {
        ctx!.fillStyle = hex(c2, 0.55);
        ctx!.beginPath();
        ctx!.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    function step(t: number) {
      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < -10) d.x = width + 10;
        if (d.x > width + 10) d.x = -10;
        if (d.y < -10) d.y = height + 10;
        if (d.y > height + 10) d.y = -10;
      }
      draw(t);
      raf = window.requestAnimationFrame(step);
    }

    resize();
    window.addEventListener("resize", resize);
    if (reduced) draw(0);
    else raf = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className={`net-canvas ${dim ? "dim" : ""}`} aria-hidden="true" />;
}
